import axios from "axios";
import { getSlug, setHrefWithSlugHook } from "../config/routes/guards/slug";
import {
  CPANEL_TOKEN_KEY,
  clearCpanelToken,
  clearCentralUser,
  logoutCpanel,
} from "../helpers/cpanelAuth";
import {
  clearHospitalToken,
  getHospitalToken,
  isHospitalLoginRoute,
} from "../helpers/hospitalAuth";
import { getApiErrorMessage } from "../helpers/apiErrorMessage";
import { isApiDebugEnabled, logApiError } from "../helpers/apiDebug";
import { MySwal } from "./swal";

const isCpanelPath = () => {
  const path = window.location.pathname;
  return path === "/" || path.startsWith("/cpanel");
};

// Ditandai true bila interceptor sudah menampilkan pesannya sendiri, supaya
// komponen pemanggil tidak menampilkan alert ganda.
const markHandled = error => {
  if (error && typeof error === "object") {
    error.isHandledByInterceptor = true;
  }
  return error;
};

/**
 * Saat mode debug aktif, semua logout & redirect otomatis ditahan supaya
 * pesan error di console sempat dibaca. Aktifkan lewat console:
 *   speakDebug.on()
 */
const holdRedirectForDebug = (error, aksiYangDitahan) => {
  if (!isApiDebugEnabled()) return false;

  const detail = error?.response
    ? `${error.response.status} ${error.response.data?.code || ''}`.trim()
    : (error?.code || 'tanpa balasan');

  MySwal.fire({
    icon: 'info',
    title: 'Mode Debug — Redirect Ditahan',
    html:
      `<div style="text-align:left">` +
      `<p><b>${aksiYangDitahan}</b> ditahan karena mode debug aktif.</p>` +
      `<p>Penyebab: <code>${detail}</code></p>` +
      `<p>Buka <b>Console</b> dan cari baris <code>[SPEAK][API ERROR]</code>, ` +
      `atau ketik <code>speakDebug.last()</code>.</p>` +
      `<p style="margin-top:8px">Matikan lagi dengan <code>speakDebug.off()</code>.</p>` +
      `</div>`,
    confirmButtonText: 'Mengerti',
    allowOutsideClick: false,
  });

  return true;
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

instance.interceptors.request.use(
  async config => {
    if (isCpanelPath()) {
      const cpanelToken = localStorage.getItem(CPANEL_TOKEN_KEY);
      if (cpanelToken) {
        config.headers.Authorization = `Bearer ${cpanelToken}`;
      }
      return config;
    }

    const slug = getSlug();

    config.headers.slug = slug;

    const hospitalToken = getHospitalToken();
    if (hospitalToken) {
      config.headers.Authorization = `Bearer ${hospitalToken}`;
    }

    if (slug && !(config.url && config.url.endsWith("/validate"))) {
      try {
        await axios.get(`${process.env.REACT_APP_API_URL}/validate`, {
          headers: {
            'slug': slug,
          },
        });
      } catch (error) {
        const status = error.response?.status;
        const code = error.response?.data?.code;

        logApiError(error, { sumber: 'preflight /validate', slug });

        // 422 = slug rumah sakit memang tidak dikenal -> keluarkan dari aplikasi.
        // Backend kini HANYA memakai 422 untuk kasus tersebut; gangguan server
        // (database mati, disk penuh) dibalas 5xx supaya user tidak terlempar
        // keluar padahal sesinya masih sah.
        if (status === 422 && (!code || code === 'INVALID_SLUG')) {
          if (holdRedirectForDebug(error, 'Redirect keluar aplikasi (slug tidak dikenal)')) {
            return Promise.reject(markHandled(error));
          }
          const pathname = window.location.pathname || '';
          window.location.href = pathname.startsWith('/hospitals/') ? '/welcome' : '/';
          return Promise.reject(markHandled(error));
        }

        // Gangguan sementara pada pengecekan tenant tidak boleh membatalkan
        // request aslinya. Biarkan request berjalan; endpoint tujuan tetap
        // melakukan pemeriksaan otorisasinya sendiri.
        console.warn(
          '[axios] pengecekan tenant gagal, request tetap dilanjutkan:',
          getApiErrorMessage(error)
        );
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

instance.interceptors.response.use(
  response => response,
  async error => {
    // Dicatat SEBELUM redirect apa pun, supaya jejaknya tidak hilang saat
    // halaman berpindah. Bisa dibaca lagi lewat speakDebug.last().
    logApiError(error, { sumber: 'response interceptor' });

    if (error.response?.status === 503 && isCpanelPath()) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && isCpanelPath()) {
      if (holdRedirectForDebug(error, 'Logout cpanel')) {
        return Promise.reject(markHandled(error));
      }
      if (window.location.pathname.startsWith('/cpanel')) {
        logoutCpanel({ hardRedirect: true });
      } else {
        clearCpanelToken();
        clearCentralUser();
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !isCpanelPath()) {
      // Hanya 401 yang berarti sesi/token memang tidak sah. Gangguan server
      // sekarang dibalas 5xx oleh backend sehingga tidak sampai ke sini.
      if (isHospitalLoginRoute()) {
        return Promise.reject(error);
      }

      if (holdRedirectForDebug(error, 'Logout & redirect ke /login')) {
        return Promise.reject(markHandled(error));
      }

      clearHospitalToken();
      MySwal.fire({
        icon: 'warning',
        title: 'Sesi Berakhir',
        text: getApiErrorMessage(
          error,
          'Sesi Anda sudah berakhir. Silakan login kembali.'
        ),
        confirmButtonText: 'Login Kembali',
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => setHrefWithSlugHook('/login'));

      return Promise.reject(markHandled(error));
    }

    if (error.response && error.response.status === 403) {
      // Sebagian endpoint memakai 403 untuk aturan bisnis, bukan untuk sesi
      // yang tidak sah -- misalnya group tertentu yang memang tidak boleh
      // mengunduh dokumen. Request seperti itu menandai dirinya sendiri lewat
      // `skipForbiddenLogout` supaya user tidak ikut ter-logout, dan pemanggil
      // yang menampilkan pesannya sendiri.
      if (error.config?.skipForbiddenLogout) {
        return Promise.reject(error);
      }

      // PENTING: 403 hanya boleh mengakhiri sesi bila memang APLIKASI KITA yang
      // menyatakan akses ditolak (balasan JSON dengan message/code). 403 yang
      // datang dari perangkat di depan aplikasi -- reverse proxy, WAF,
      // ModSecurity, Cloudflare -- balasannya HTML atau kosong, dan sesi user
      // sebenarnya masih sah. Sebelumnya SEMUA 403 memicu logout, sehingga satu
      // request yang diblokir WAF cukup untuk menendang user keluar.
      const body = error.response.data;
      const dariAplikasi = Boolean(
        body && typeof body === 'object' && (body.message || body.code)
      );

      if (!dariAplikasi) {
        MySwal.fire({
          icon: 'error',
          title: 'Permintaan Diblokir',
          text:
            'Permintaan ditolak (403) oleh perangkat keamanan jaringan sebelum sampai ke aplikasi. ' +
            'Sesi Anda masih aktif. Silakan hubungi administrator IT dengan menyebutkan alamat ' +
            'permintaan ini agar aturan firewall/WAF-nya diperiksa.',
          confirmButtonText: 'OK',
        });
        return Promise.reject(markHandled(error));
      }

      if (holdRedirectForDebug(error, 'Logout karena akses ditolak')) {
        return Promise.reject(markHandled(error));
      }

      const errorMessage = getApiErrorMessage(
        error,
        'Anda tidak memiliki akses untuk mengakses halaman/fitur ini.'
      );

      MySwal.fire({
        icon: 'error',
        title: 'Akses Ditolak!',
        text: errorMessage,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'swal2-noanimation'
        }
      }).then(async () => {
        try {
          if (isCpanelPath()) {
            logoutCpanel({ hardRedirect: true });
            return;
          }

          const token = localStorage.getItem(`token${getSlug() ? '-' + getSlug() : ''}`);
          if (token) {
            await axios({
              url: '/users/logout',
              method: 'POST',
              headers: {
                'Access-Control-Allow-Origin': '*',
                Authorization: `Bearer ${token}`,
              },
            }).catch(() => {});
          }
        } catch (err) {
          // Ignore logout errors
        } finally {
          if (!isCpanelPath()) {
            clearHospitalToken();
            setHrefWithSlugHook('/login');
          }
        }
      });

      return Promise.reject(markHandled(error));
    }

    return Promise.reject(error);
  }
);

export default instance;
